import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Home, UtensilsCrossed, Bath, Paintbrush, Layers, Lightbulb } from "lucide-react";

export default function ServicesSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: Home,
      title: "Komplet Renovering",
      description: "Fuld renovering af hele boligen med fokus på de områder, der giver størst værdiforøgelse.",
    },
    {
      icon: UtensilsCrossed,
      title: "Køkken",
      description: "Moderne køkkenrenovering der tiltrækker købere og øger boligens salgspris markant.",
    },
    {
      icon: Bath,
      title: "Badeværelse",
      description: "Stilfulde badeværelser der gør indtryk og sikrer et hurtigt salg.",
    },
    {
      icon: Paintbrush,
      title: "Maling & Finish",
      description: "Professionel maling og finish der giver boligen det sidste touch.",
    },
    {
      icon: Layers,
      title: "Gulve",
      description: "Nye gulve eller renovering af eksisterende for et friskt og moderne udtryk.",
    },
    {
      icon: Lightbulb,
      title: "Belysning",
      description: "Strategisk belysning der fremhæver boligens bedste kvaliteter.",
    },
  ];

  return (
    <section 
      id="services" 
      ref={ref}
      className="py-24 md:py-32 bg-secondary/30"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-semibold uppercase tracking-widest text-accent">
            Vores Services
          </span>
          <h2 
            className="mt-4 text-4xl md:text-5xl font-bold text-foreground tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Strategisk renovering
            <br />
            <span className="text-primary">for maksimal værdi</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground">
            Vi fokuserer på de renoveringer, der giver det største afkast ved salg. 
            Hvert projekt er skræddersyet til din boligs potentiale.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white p-8 border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300"
              data-testid={`service-card-${index}`}
            >
              <div className="w-12 h-12 bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                <service.icon className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <h3 
                className="text-xl font-semibold text-foreground mb-3"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {service.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
